import { useForm, Controller } from 'react-hook-form';
import {
  Modal,
  Input,
  PrimaryButton,
  SecondaryButton,
  Typography,
} from '@weareopenr/component-library';
import { IExtension, IUpdateExtensionRequest } from 'src/api/extensions';
import { useUpdateExtension } from 'src/hooks/useExtensions';
import styles from './EditAuthModal.module.scss';

interface IEditAuthModalProps {
  open: boolean;
  onClose: () => void;
  brandId: string;
  extension: IExtension;
}

interface IFormValues {
  username?: string;
  password?: string;
  access_token?: string;
  contents?: string;
}

export const EditAuthModal = ({ open, onClose, brandId, extension }: IEditAuthModalProps) => {
  const updateMutation = useUpdateExtension(onClose);
  const authType = extension.authentication?.type;

  const { control, handleSubmit } = useForm<IFormValues>({
    defaultValues: {
      username: '',
      password: '',
      access_token: '',
      contents: '',
    },
  });

  const onSubmit = (data: IFormValues) => {
    let payload: IUpdateExtensionRequest;

    if (authType === 'BASIC' && data.username && data.password) {
      payload = {
        authentication: {
          type: 'BASIC',
          credentials: { username: data.username, password: data.password },
        },
      };
    } else if (authType === 'TOKEN' && data.access_token) {
      payload = {
        authentication: {
          type: 'TOKEN',
          credentials: { access_token: data.access_token },
        },
      };
    } else if (authType === 'CERTIFICATE' && data.contents) {
      payload = {
        authentication: {
          type: 'CERTIFICATE',
          credentials: { contents: data.contents },
        },
      };
    } else {
      return;
    }

    updateMutation.mutate({
      brandId,
      extensionId: extension.id,
      payload,
    });
  };

  return (
    <Modal closeModal={onClose} open={open}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Typography size="xxlarge" weight="semi-bold" className={styles['heading']}>
          Edit Authentication
        </Typography>
        <Typography className={styles['subtitle']}>{extension.addon.name}</Typography>

        {authType === 'BASIC' && (
          <>
            <div className={styles['form-group']}>
              <Controller
                name="username"
                control={control}
                rules={{ required: 'Username is required' }}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    id={field.name}
                    type="text"
                    label="Username"
                    error={fieldState.error?.message}
                  />
                )}
              />
            </div>
            <div className={styles['form-group']}>
              <Controller
                name="password"
                control={control}
                rules={{ required: 'Password is required' }}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    id={field.name}
                    type="password"
                    label="Password"
                    placeholder="Enter new password"
                    error={fieldState.error?.message}
                  />
                )}
              />
            </div>
          </>
        )}

        {authType === 'TOKEN' && (
          <div className={styles['form-group']}>
            <Controller
              name="access_token"
              control={control}
              rules={{ required: 'Access token is required' }}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  id={field.name}
                  type="text"
                  label="Access Token"
                  placeholder="Enter new access token"
                  error={fieldState.error?.message}
                />
              )}
            />
          </div>
        )}

        {authType === 'CERTIFICATE' && (
          <div className={styles['form-group']}>
            <Controller
              name="contents"
              control={control}
              rules={{ required: 'Certificate contents is required' }}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  id={field.name}
                  type="text"
                  label="Certificate Contents"
                  placeholder="Enter new certificate"
                  error={fieldState.error?.message}
                />
              )}
            />
          </div>
        )}

        <div className={styles['controls']}>
          <SecondaryButton onClick={onClose} disabled={updateMutation.isPending}>
            Cancel
          </SecondaryButton>
          <PrimaryButton type="submit" loading={updateMutation.isPending}>
            Save Changes
          </PrimaryButton>
        </div>
      </form>
    </Modal>
  );
};
